// Adapted from Klargo-Reference/pages/Support.tsx ("/support" — the
// authenticated home of the canonical help register): an intro band on the
// full rail, then a 232px sticky aside (context strip + Kontakt) beside a
// 720px reading column holding the register (three chapters, ruled question
// rows).
import React from 'react';
import AuthShell from '../components/ds/AuthShell';
import { ForwardArrow } from '../components/ds/StatusMark';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { useLanguage } from '../components/layout/LanguageSwitcher';
import { HELP_CHAPTERS, helpQuestionKeys, helpRoutesFor } from '../content/help/helpRegister';

const TextRoute = ({ to, href, children }) =>
  href ? (
    <a href={href} className="ds-action-text">
      {children}
      <ForwardArrow stroke="currentColor" width={14} height={11} />
    </a>
  ) : (
    <a href={to} className="ds-action-text">
      {children}
      <ForwardArrow stroke="currentColor" width={14} height={11} />
    </a>
  );

export default function SupportPage({ t }) {
  const { t: tp } = useLanguage();

  return (
    <AuthShell t={t}>
      <div className="ds-animate-page ds-auth-rail pb-[var(--ds-page-bottom)] pt-[var(--ds-page-top)]">
        <div className="max-w-[1008px] border-b border-ds-rule-row pb-8">
          <h1 className="ds-title-route m-0 mb-1.5">{tp('ds.support.title')}</h1>
          <p className="ds-body m-0 max-w-[54ch]">{tp('support.intro')}</p>
        </div>

        <div className="pt-9 lg:grid lg:grid-cols-[232px_minmax(0,720px)] lg:items-stretch lg:gap-x-14">
          <aside className="mb-10 lg:col-start-1 lg:row-start-1 lg:mb-0">
            <div className="lg:sticky lg:top-[84px]">
              <div className="mb-7">
                <div className="mb-1 text-[13px] font-medium uppercase tracking-[0.04em] text-ds-muted">
                  {tp('support.context.none.title')}
                </div>
                <div className="mt-1 flex flex-col items-start">
                  <TextRoute to="/Profile#license-section">{tp('support.route.license')}</TextRoute>
                </div>
              </div>
              <div className="border-t border-ds-rule-row pt-6">
              <div className="mb-2 text-[13px] font-medium uppercase tracking-[0.04em] text-ds-muted">
                {tp('support.contact.title')}
              </div>
              <p className="m-0 text-[16px] font-semibold leading-[1.35] text-ds-ink">support@klargo.se</p>
              <p className="m-0 mt-1 text-[14.5px] leading-[1.45] text-ds-text">{tp('ds.support.contactHours')}</p>
              <p className="m-0 mt-2 max-w-[34ch] text-[14px] leading-[1.5] text-ds-text">
                {tp('help.contact.limit')}
              </p>
              <TextRoute href="mailto:support@klargo.se">{tp('help.contact.cta')}</TextRoute>
              </div>
            </div>
          </aside>

          <div className="min-w-0 space-y-[58px] lg:col-start-2 lg:row-start-1">
            {HELP_CHAPTERS.map((chapter) => (
              <section key={chapter.id} id={chapter.id} aria-labelledby={`support-chapter-${chapter.id}`}>
                <h2 id={`support-chapter-${chapter.id}`} className="ds-title-section m-0 mb-2">
                  {tp(chapter.titleKey)}
                </h2>
                <p className="ds-body m-0 mb-[18px] max-w-[62ch]">{tp(chapter.leadKey)}</p>

                <Accordion type="single" collapsible className="w-full">
                  {chapter.items.map((id) => {
                    const keys = helpQuestionKeys(id);
                    const routes = helpRoutesFor(id, 'authenticated');
                    return (
                      <AccordionItem key={id} value={id} className="border-b border-ds-rule-row last:border-b-0">
                        <AccordionTrigger className="items-start gap-6 pb-[15px] pt-[17px] text-left hover:no-underline [&>svg]:mt-[5px] [&>svg]:h-[14px] [&>svg]:w-[14px] [&>svg]:shrink-0 [&>svg]:stroke-[1.6] [&>svg]:text-ds-muted">
                          <span className="font-ds-serif text-[16.5px] font-semibold leading-[1.35] text-ds-ink">
                            {tp(keys.question)}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="max-w-[62ch] pb-[26px] pr-6 pt-0.5 text-[15.5px] leading-[1.65] text-ds-text">
                          <p className="m-0">{tp(keys.answer)}</p>
                          {routes.length > 0 && (
                            <div className="mt-0.5 flex flex-col items-start">
                              {routes.map((route) => (
                                <TextRoute
                                  key={route.to}
                                  {...(route.external ? { href: route.to } : { to: route.to })}
                                >
                                  {tp(route.labelKey)}
                                </TextRoute>
                              ))}
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </section>
            ))}
          </div>
        </div>
      </div>
    </AuthShell>
  );
}
